import DOMPurify from 'dompurify'

/**
 * Tags allowed in AI-generated and user-authored rich text (offer descriptions,
 * rendered markdown). Deliberately excludes form, media and embed elements.
 */
const RICH_TEXT_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'pre', 'code',
  'a',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
]

const RICH_TEXT_ATTRS = ['href', 'title', 'target', 'rel', 'class', 'colspan', 'rowspan', 'align']

/** Tags allowed in previews that are plain text with line breaks only. */
const TEXT_ONLY_TAGS = ['br']

/** Blocks `javascript:`, `data:` and other script-capable URL schemes on links. */
const SAFE_URI_SCHEMES = /^(?:https?|mailto|tel):|^[^a-z]*(?:[#/?]|$)/i

/**
 * Anchors that open in a new tab must not be able to reach back into this
 * window via `window.opener`.
 */
let hookInstalled = false
function installAnchorHook(): void {
  if (hookInstalled) return
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('target')) {
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })
  hookInstalled = true
}

/** Sanitize rich HTML, keeping formatting, lists, tables and links. */
export function sanitizeRichHtml(html: string | null | undefined): string {
  if (!html) return ''
  installAnchorHook()
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ATTRS,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: SAFE_URI_SCHEMES,
    // Exempt non-URL attributes from the scheme check so link targets survive it.
    ADD_URI_SAFE_ATTR: ['target', 'rel', 'class', 'align'],
  })
}

/** Sanitize content that should render as text plus <br> line breaks. */
export function sanitizeTextWithBreaks(text: string | null | undefined): string {
  if (!text) return ''
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: TEXT_ONLY_TAGS,
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  })
}
